/**
 * AI Advisor Panel — Model-agnostic sales advisor for lead detail sidebar
 */

import React, { useState, useEffect, useMemo } from 'react';
import { query, orderBy, onSnapshot } from 'firebase/firestore';
import { leadCommentsCollectionRef } from '../../utils/leadCommentsFirestore';
import { useCRM } from '../../utils/CRMContext';
import { Sparkles, Copy, Check, Loader2, AlertCircle } from 'lucide-react';
import {
    askAdvisor,
    getAvailableModels,
    ADVISOR_CONTEXTS,
    ProviderOption,
} from '../../utils/salesAdvisorService';
import { saveAiAdvisorPrefs, getAiAdvisorPrefs } from '../../utils/appSettings';
import type { Lead, LeadComment } from '../../types';
import './AIAdvisorPanel.css';

interface AIAdvisorPanelProps {
    lead: Lead;
}

const AIAdvisorPanel: React.FC<AIAdvisorPanelProps> = ({ lead }) => {
    // State
    const [models, setModels] = useState<ProviderOption[]>([]);
    const [selectedModelIdx, setSelectedModelIdx] = useState(0);
    const [contextId, setContextId] = useState(ADVISOR_CONTEXTS[0].id);
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState('');
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [comments, setComments] = useState<LeadComment[]>([]);

    const { enhancedDeals } = useCRM();

    // Get deals for this lead
    const leadDeals = useMemo(() => {
        return enhancedDeals.filter(d => d.leadId === lead.id);
    }, [enhancedDeals, lead.id]);

    // Subscribe to comments for this lead
    useEffect(() => {
        if (!lead.id) return;
        const commentsRef = leadCommentsCollectionRef(lead.id);
        const q = query(commentsRef, orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date(),
                updatedAt: doc.data().updatedAt?.toDate(),
            })) as LeadComment[];
            setComments(data);
        });
        return () => unsubscribe();
    }, [lead.id]);

    // Load available models & saved preference
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const available = await getAvailableModels();
            if (cancelled) return;
            setModels(available);

            if (available.length > 0) {
                const prefs = await getAiAdvisorPrefs();
                const savedIdx = available.findIndex(
                    m => m.provider === prefs.provider && m.model === prefs.model
                );
                setSelectedModelIdx(savedIdx >= 0 ? savedIdx : 0);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Handle model change — persist preference
    const handleModelChange = (idx: number) => {
        setSelectedModelIdx(idx);
        const model = models[idx];
        if (model) {
            saveAiAdvisorPrefs(model.provider, model.model);
        }
    };

    // Ask the AI
    const handleAsk = async () => {
        if (models.length === 0) {
            setError('No AI provider configured. Add an API key in Settings (Gemini, OpenAI, or Anthropic).');
            return;
        }

        const selectedModel = models[selectedModelIdx];
        const context = ADVISOR_CONTEXTS.find(c => c.id === contextId);
        if (!selectedModel || !context) return;

        setLoading(true);
        setResponse('');
        setError('');
        setCopied(false);

        try {
            const result = await askAdvisor(
                selectedModel,
                lead,
                leadDeals,
                comments,
                context.prompt,
            );
            setResponse(result.content);
        } catch (err: any) {
            setError(err.message || 'Something went wrong. Try again.');
        } finally {
            setLoading(false);
        }
    };

    // Copy response to clipboard
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(response);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Fallback
            const textarea = document.createElement('textarea');
            textarea.value = response;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    // Format response text (basic markdown)
    const formatResponse = (text: string) => {
        return text
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/^### (.+)$/gm, '<h4>$1</h4>')
            .replace(/^## (.+)$/gm, '<h3>$1</h3>')
            .replace(/^# (.+)$/gm, '<h2>$1</h2>')
            .replace(/^- (.+)$/gm, '<li>$1</li>')
            .replace(/(<li>.*?<\/li>)/g, '<ul>$1</ul>')
            .replace(/<\/ul>\s*<ul>/g, '')
            .replace(/\n{2,}/g, '<br/><br/>')
            .replace(/\n/g, '<br/>');
    };

    const noProviders = models.length === 0;

    return (
        <div className="ai-advisor-panel">
            <div className="ai-advisor-header">
                <div className="ai-advisor-title">
                    <Sparkles size={18} />
                    <span>Insights</span>
                </div>
            </div>

            <div className="ai-advisor-controls">
                {/* Model selector */}
                <div className="ai-advisor-field">
                    <label>Model</label>
                    <select
                        value={selectedModelIdx}
                        onChange={(e) => handleModelChange(Number(e.target.value))}
                        disabled={loading || noProviders}
                        className="ai-advisor-select"
                    >
                        {noProviders ? (
                            <option>No API keys configured</option>
                        ) : (
                            models.map((m, idx) => (
                                <option key={`${m.provider}-${m.model}`} value={idx}>
                                    {m.label}
                                </option>
                            ))
                        )}
                    </select>
                </div>

                {/* Context selector */}
                <div className="ai-advisor-field">
                    <label>Ask about</label>
                    <select
                        value={contextId}
                        onChange={(e) => setContextId(e.target.value)}
                        disabled={loading}
                        className="ai-advisor-select"
                    >
                        {ADVISOR_CONTEXTS.map(ctx => (
                            <option key={ctx.id} value={ctx.id}>{ctx.label}</option>
                        ))}
                    </select>
                </div>

                <button
                    className="ai-advisor-ask-btn"
                    onClick={handleAsk}
                    disabled={loading || noProviders}
                >
                    {loading ? (
                        <>
                            <span className="ai-advisor-spinner" />
                            Thinking…
                        </>
                    ) : (
                        'Ask AI'
                    )}
                </button>
            </div>

            {/* Response area */}
            <div className="ai-advisor-response-area">
                {error && (
                    <div className="ai-advisor-error">
                        <span>⚠️</span> {error}
                    </div>
                )}

                {loading && !response && (
                    <div className="ai-advisor-loading">
                        <div className="ai-advisor-pulse" />
                        <span>Analyzing {lead.firstName}'s profile…</span>
                    </div>
                )}

                {response && (
                    <div className="ai-advisor-response">
                        <div className="ai-advisor-response-header">
                            <span className="ai-advisor-model-badge">
                                {models[selectedModelIdx]?.label || 'AI'}
                            </span>
                            <button
                                className={`ai-advisor-copy-btn ${copied ? 'copied' : ''}`}
                                onClick={handleCopy}
                                title="Copy to clipboard"
                            >
                                {copied ? '✓ Copied' : '📋 Copy'}
                            </button>
                        </div>
                        <div
                            className="ai-advisor-text"
                            dangerouslySetInnerHTML={{ __html: formatResponse(response) }}
                        />
                    </div>
                )}

                {!loading && !response && !error && (
                    <div className="ai-advisor-empty">
                        <p>Select a question type and click <strong>Ask AI</strong> to get personalized advice for this lead.</p>
                        <div className="ai-advisor-lead-summary">
                            <div><strong>{lead.firstName} {lead.lastName}</strong></div>
                            <div>{lead.projectType || 'No project type'} · {lead.location || 'No location'}</div>
                            {leadDeals.length > 0 && (
                                <div>{leadDeals.length} deal{leadDeals.length > 1 ? 's' : ''} · ${leadDeals.reduce((s, d) => s + (d.value || 0), 0).toLocaleString()}</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AIAdvisorPanel;
